import { Dialog, Switch } from "construct-ui";
import SelectCodeMasterCategoryComponent from "../../components/code-master/select-code-master-category.component";
import ToggleButtonGroupComponent, { BUTTON_TYPE } from "../../components/common/toggle-button-group.component";
import SortableMultiSelectComponent from "../../components/multi-select/sortable-multi-select.component";
import DynamicReportFilterComponent from "../../components/report/dynamic-report-filter.component";
import SingleSelectComponent from "../../components/single-select/single-select.component";
import { codeMasterType } from "../../config/constants";
import ReportModel from "../../models/report/report-model.model";
import ViewBase from "../view-base.view";

/**
 * A view for mobile report builder
 * 
 * @author CV Leong
 */
export default function MobileReportBuilderView() {
  ViewBase.call(this);

  /**
   * Cached data to avoid repetitive API calls
   */
  const cachedPeriods = new Map();
  const cachedReportInfo = new Map();
  const cachedMobileTemplates = new Map();

  const KEYS_SEPARATOR = '^^';
  const KEY_VALUE_SEPARATOR = ':=';
  const VALUES_SEPARATOR = '^';

  const MODE = {
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
  }

  const REPORT_TEMPLATE_TYPE = {
    PERSONAL: 'Personal',
    PUBLIC: 'Public',
  }

  const DEFAULT_PERIOD_ORDER = [
    'M', // Months - L3M
    'W', // Weeks - L4W
  ];

  const DEFAULT_REPORT_FORM = Object.freeze({
    id: null,                             // Report ID
    name: null,                           // Report name
    type: REPORT_TEMPLATE_TYPE.PERSONAL,  // Report type
    dataSourceCode: null,                 // Code of selected data source
    period: null,                         // Report period type
    includeCurrent: false,                // Whether to include current week/month depending on period selection or not
    createTodoLinkCode: false,            // Whether to generate `Link Code` upon report creation or not
    linkCode: '',                         // Generated `Link Code`
    userRoleList: [],                     // Authorized user roles
    filters: null,                        // Filters selected from `DynamicReportFilterComponent`
    parameters: '',                       // Filters selected from `DynamicReportFilterComponent`
    xAxis: null,                          // Column header
    yAxis: [],                            // Row headers
    zAxis: [],                            // Data display
    yAxisOrderedCodes: [],                // Order of row header codes
    zAxisOrderedCodes: [],                // Order of data display codes
    addColumnTotal: true,                 // Setting to show/hide column total
    addRowTotal: false,                   // Setting to show/hide row total
    excludeEmptyRow: true,                // Setting to show/hide empty rows
    showFilter: false,                    // Setting to show/hide filter
    zAxisLocation: 'Y',                   // Setting for position of z-axis. 'Y' = top | 'N' = bottom | 'L' = Left (currently not in use)
  });

  const DEFAULT_LOAD_REPORT_FORM = Object.freeze({
    dataSourceCode: null,                 // Code of selected data source
    period: null,                         // Report period type
    selectedReport: null,                 // Selected mobile report
    type: REPORT_TEMPLATE_TYPE.PERSONAL,  // Report type
  });

  let dataSourceOptions;
  let isOpenLoadReportTemplateDialog = false;
  let loadReportPeriodOptions;
  let mobileReportOptions;
  let mode = MODE.CREATE;
  let periodOptions;
  let reportCreateUpdateForm = createReportFormObject();
  let reportLoadForm = newReportLoadForm();
  let xAxisOptions;
  let yAxisOptions;
  let zAxisOptions;
  let isLoadedFromTemplate = false;

  let stringifyFilterParams = (reportFilters) => {
    if (!reportFilters) return '';

    let _paramsArray = reportFilters.map(filter => {
      let value = '';

      // Stringify the filter.value(s) - start
      if (filter.value === null || filter.value === undefined) {
        value = ''
      } else if (['string', 'boolean'].includes(typeof filter.value)) {
        value = filter.value;
      } else if (Array.isArray(filter.value)) {

        let valueArray = filter.value.map(v => {
          if (v === null || v === undefined) {
            return ''
          }

          if (['string', 'boolean'].includes(typeof v)) {
            return v;
          }

          if (v.code) {
            return v.code
          }

          throw `Unexpected value type in '${filter.key}': ${filter.value}`
        });
        // Stringify the filter.value(s) - end
        value = valueArray.join(VALUES_SEPARATOR);

      } else if (filter.value.code) {
        value = filter.value.code;
      }

      return filter.key + KEY_VALUE_SEPARATOR + value
    })

    return _paramsArray.join(KEYS_SEPARATOR);
  }

  function createReportFormObject() {
    const properties = structuredClone(DEFAULT_REPORT_FORM);
    Object.seal(properties);

    return {
      // Getters
      get id() { return properties.id; },
      get name() { return properties.name; },
      get type() { return properties.type; },
      get dataSourceCode() { return properties.dataSourceCode; },
      get period() { return properties.period; },
      get includeCurrent() { return properties.includeCurrent; },
      get createTodoLinkCode() { return properties.createTodoLinkCode; },
      get linkCode() { return properties.linkCode; },
      get userRoleList() { return properties.userRoleList; },
      get filters() { return properties.filters; },
      get parameters() { return properties.parameters; },
      get xAxis() { return properties.xAxis; },
      get yAxis() { return properties.yAxis; },
      get zAxis() { return properties.zAxis; },
      get yAxisOrderedCodes() { return properties.yAxisOrderedCodes; },
      get zAxisOrderedCodes() { return properties.zAxisOrderedCodes; },
      get addColumnTotal() { return properties.addColumnTotal; },
      get addRowTotal() { return properties.addRowTotal; },
      get excludeEmptyRow() { return properties.excludeEmptyRow; },
      get showFilter() { return properties.showFilter; },
      get zAxisLocation() { return properties.zAxisLocation; },

      // Setters
      set id(val) { properties.id = val; },
      set name(val) { properties.name = val; },
      set type(val) { properties.type = val; },
      set dataSourceCode(val) { properties.dataSourceCode = val; },
      set period(val) { properties.period = val; },
      set includeCurrent(val) { properties.includeCurrent = val; },
      set createTodoLinkCode(val) { properties.createTodoLinkCode = val; },
      set linkCode(val) { properties.linkCode = val; },
      set userRoleList(val) { properties.userRoleList = val; },
      set filters(val) {
        properties.filters = val;
        properties.parameters = stringifyFilterParams(val);
      },
      set xAxis(val) { properties.xAxis = val; },
      set yAxis(val) { properties.yAxis = val; },
      set zAxis(val) { properties.zAxis = val; },
      set yAxisOrderedCodes(val) { properties.yAxisOrderedCodes = val; },
      set zAxisOrderedCodes(val) { properties.zAxisOrderedCodes = val; },
      set addColumnTotal(val) { properties.addColumnTotal = val; },
      set addRowTotal(val) { properties.addRowTotal = val; },
      set excludeEmptyRow(val) { properties.excludeEmptyRow = val; },
      set showFilter(val) { properties.showFilter = val; },
      set zAxisLocation(val) { properties.zAxisLocation = val; },

      // Methods - start

      getParams: () => ({
        name: properties.name,
        type: properties.type,
        dataSourceCode: properties.dataSourceCode,
        period: properties.period,
        includeCurrent: properties.includeCurrent,
        createLinkCode: properties.createTodoLinkCode,
        userRoles: properties.userRoleList ? properties.userRoleList.map(r => r.code).join(KEYS_SEPARATOR) : '',
        parameters: properties.parameters,
        xAxis: properties.xAxis?.code ?? '',
        yAxis: properties.yAxisOrderedCodes.join(KEYS_SEPARATOR),
        zAxis: properties.zAxisOrderedCodes.join(KEYS_SEPARATOR),
        addColumnTotal: properties.addColumnTotal,
        addRowTotal: properties.addRowTotal,
        excludeEmptyRow: properties.excludeEmptyRow,
        showFilter: properties.showFilter,
        zAxisLocation: properties.zAxisLocation,
      }),

      resetToDuplicate() {
        this.createTodoLinkCode = false;
        this.id = null;
        this.name = `Copy of ${this.name}`;
        this.linkCode = '';
      },

      /**
       * This method does not reset Report Information
       */
      resetFilters() {
        properties.userRoleList = [];
        properties.filters = null;
        properties.xAxis = null;
        properties.yAxis = [];
        properties.zAxis = [];
        properties.yAxisOrderedCodes = [];
        properties.zAxisOrderedCodes = [];
        properties.addColumnTotal = true;
        properties.addRowTotal = false;
        properties.excludeEmptyRow = true;
        properties.showFilter = false;
        properties.zAxisLocation = 'Y';
      },

      load(data) {
        isLoadedFromTemplate = true;
        Object.assign(properties, DEFAULT_REPORT_FORM);
        Object.keys(properties).forEach((key) => {
          if (key in data) {

            if (key === 'xAxis') {
              properties[key] = data[key] ? { code: data[key], name: data[key] } : null;
              return;
            }

            if (key === 'yAxis') {
              properties[key] = data[key] ? data[key].split(KEYS_SEPARATOR).map(v => ({ code: v, name: v })) : [];
              properties[key + 'OrderedCodes'] = data[key] ? data[key].split(KEYS_SEPARATOR) : [];
              return;
            }

            if (key === 'zAxis') {
              properties[key] = data[key] ? data[key].split(KEYS_SEPARATOR).map(v => ({ code: v, name: v.split(KEY_VALUE_SEPARATOR).at(0) })) : [];
              properties[key + 'OrderedCodes'] = data[key] ? data[key].split(KEYS_SEPARATOR) : [];
              return;
            }

            if (key === 'filters') {
              this.filters = data[key];
            }

            properties[key] = data[key];
          }
        });
      },

      // Methods - end
    }
  }

  function newReportLoadForm() {
    const properties = structuredClone(DEFAULT_LOAD_REPORT_FORM);

    return {
      // Getters
      get dataSourceCode() { return properties.dataSourceCode; },
      get selectedReport() { return properties.selectedReport; },
      get period() { return properties.period; },
      get type() { return properties.type; },

      // Setters
      set dataSourceCode(val) { properties.dataSourceCode = val; },
      set selectedReport(val) { properties.selectedReport = val; },
      set period(val) { properties.period = val; },
      set type(val) { properties.type = val; },

      // Methods - start

      getParams: () => ({
        dataSourceCode: properties.dataSourceCode,
        periodCode: properties.period,
        templateType: properties.type,
      }),

      validate: () => {
        if (!properties.dataSourceCode || !properties.period || !properties.type) {
          return false;
        }
        return true;
      },

      // Methods - end

    }
  }

  const handleReportFormSubmission = (e) => {
    // Form validation
    if (!this.validateForm(e, '#mobile-report-builder-form.needs-validation')) {
      return false;
    }

    this.showLoading();

    ReportModel.saveMobileReportTemplate(reportCreateUpdateForm.getParams())
      .then((result) => {
        this.showMessage(result.message);
        mode = mode === MODE.CREATE ? MODE.UPDATE : mode;
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  const getDataSourceOptions = () => {
    if (dataSourceOptions?.length > 0) {
      return Promise.resolve(dataSourceOptions);
    }

    return ReportModel.getMobileReportDataSource()
      .then((data) => {
        dataSourceOptions = data ?? [];
        return dataSourceOptions;
      })
      .catch((error) => {
        this.handleError(error);
      })
  }

  const getDefaultPeriod = (options) => {
    const defaultPeriod = DEFAULT_PERIOD_ORDER.find(periodCode => {
      return options.some(option => option.code === periodCode);
    })

    return defaultPeriod ?? options[0]?.code;
  }

  const getPeriodOptions = (dataSourceCode) => {
    // Get period options
    let cachedData = cachedPeriods.get(dataSourceCode);
    if (cachedData) {
      return Promise.resolve(cachedData);
    };

    return ReportModel.getReportPeriodByMobileReportDataSource(dataSourceCode)
      .then((data) => {
        const _periodOptions = data ?? [];
        cachedReportInfo.set(`${dataSourceCode}`, _periodOptions);

        return _periodOptions;
      })
      .catch((error) => {
        this.handleError(error);
      })
  }

  const getReportInfo = () => {
    // Get cached report structure
    let cachedData = cachedReportInfo.get(`${reportCreateUpdateForm.dataSourceCode}-${reportCreateUpdateForm.period}`);
    if (cachedData) {
      return Promise.resolve(cachedData);
    };

    // Get report structure list via API
    return ReportModel.getReportInfo(reportCreateUpdateForm.dataSourceCode, reportCreateUpdateForm.period)
      .then((result) => {
        cachedReportInfo.set(`${reportCreateUpdateForm.dataSourceCode}-${reportCreateUpdateForm.period}`, result);
        return result;
      })
      .catch((error) => {
        this.handleError(error);
      })
  }

  const updateReportStructureOptions = () => {
    return getReportInfo()
      .then((result) => {
        xAxisOptions = result?.xAxis ?? [];
        yAxisOptions = result?.yAxis ?? [];
        zAxisOptions = result?.zAxis ?? [];
      });
  }

  const initializePageData = () => {
    this.showLoading();
    reportCreateUpdateForm = createReportFormObject();
    getDataSourceOptions()
      .then((data) => {
        return reportCreateUpdateForm.dataSourceCode = data?.at(0)?.code ?? null;
      })
      .then(getPeriodOptions)   // get periodTypes
      .then((data) => {
        // Populate select options
        periodOptions = data;
        // Set default value
        reportCreateUpdateForm.period = getDefaultPeriod(periodOptions);
      })
      .then(updateReportStructureOptions) // get report structure
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  const handleDataSourceChange = (dataSourceCode) => {
    reportCreateUpdateForm.dataSourceCode = dataSourceCode;
    this.showLoading();
    getPeriodOptions(dataSourceCode)
      .then((data) => {
        // Populate select options
        periodOptions = data;
        // Set default value
        reportCreateUpdateForm.period = getDefaultPeriod(periodOptions);
        reportCreateUpdateForm.resetFilters();
      })
      .then(updateReportStructureOptions) // get report structure
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  const handlePeriodChange = (event) => {
    reportCreateUpdateForm.period = event.target.value;
    reportCreateUpdateForm.resetFilters();
    updateReportStructureOptions();
  }

  const openLoadReportTemplateDialog = () => {
    isOpenLoadReportTemplateDialog = true;
  }

  const closeLoadReportDialog = () => {
    isOpenLoadReportTemplateDialog = false;
  }

  const initLoadReportForm = () => {
    this.showLoading();
    reportLoadForm = newReportLoadForm();
    getDataSourceOptions()
      .then((data) => {
        // Set default value
        return reportLoadForm.dataSourceCode = data?.at(0)?.code ?? null;
      })
      .then(getPeriodOptions)
      .then((data) => {
        loadReportPeriodOptions = data;
        // Set default value
        reportLoadForm.period = getDefaultPeriod(loadReportPeriodOptions);
      })
      .then(updateMobileReportList)
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  const handleReportLoadFormFieldChange = (key, value) => {
    this.showLoading();
    // Clear template selection
    reportLoadForm.selectedReport = null;

    // Set value
    reportLoadForm[key] = value;

    if (key === 'dataSourceCode') {
      getPeriodOptions(value)
        .then((data) => {
          loadReportPeriodOptions = data;
          reportLoadForm.period = getDefaultPeriod(loadReportPeriodOptions)
        })
        .then(updateMobileReportList)
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.hideLoading();
        });

      return;
    }

    updateMobileReportList()
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  }

  const updateMobileReportList = () => {
    if (!reportLoadForm.validate()) {
      return Promise.resolve();
    }

    // Get cached mobile report list
    let { dataSourceCode, periodCode, templateType } = reportLoadForm.getParams();
    let cachedData = cachedMobileTemplates.get(`${dataSourceCode}-${periodCode}-${templateType}`);
    if (cachedData) {
      mobileReportOptions = cachedData;
      return Promise.resolve(mobileReportOptions);
    };

    return ReportModel.getMobileReportTemplateList(reportLoadForm.getParams())
      .then((data) => {
        mobileReportOptions = data ?? [];
        cachedMobileTemplates.set(`${dataSourceCode}-${periodCode}-${templateType}`, mobileReportOptions)
        return mobileReportOptions;
      })
      .catch((error) => {
        mobileReportOptions = [];
        this.handleError(error);
      })
  }

  const loadMobileReport = (e) => {
    // Form validation
    if (!this.validateForm(e, '#load-report-form')) {
      return false;
    }

    this.showLoading();

    ReportModel.getMobileReportTemplate(reportLoadForm.selectedReport.id)
      .then((data) => {
        reportCreateUpdateForm.load(data);
        mode = MODE.UPDATE;
        closeLoadReportDialog();
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  const scrollToTop = () => {
    document.querySelector('html').scrollTop = 0; // Scroll to top
  }

  const resetReportForm = () => {
    // Clear validation state
    let form = document.querySelector('#mobile-report-builder-form.needs-validation');
    form.classList.remove('was-validated');

    // Reset mode & clear template selection
    mode = MODE.CREATE;
    reportLoadForm.selectedReport = null;

    // Reset form
    reportCreateUpdateForm = createReportFormObject();
    handleDataSourceChange(dataSourceOptions[0].code);
    scrollToTop();
  }

  const handleDuplicateButton = (e) => {
    e?.preventDefault();
    mode = MODE.CREATE;
    reportCreateUpdateForm.resetToDuplicate();
    reportLoadForm.selectedReport = null;
    scrollToTop();
  }

  const handleClearButton = (e) => {
    e?.preventDefault();
    resetReportForm();
  }

  const handleDeleteButton = (isLoaded = false) => (e) => {
    e?.preventDefault();

    this.showConfirm(
      this.getText('LT00000979', 'Do you want to delete the template?'),
      `${this.getText('LT00000135', 'Delete')} ${reportLoadForm.selectedReport.name}`,
      (e) => {
        if (e === 'YES') {
          ReportModel.deleteMobileReportTemplate(reportLoadForm.selectedReport.id)
            .then((result) => {

              // Remove cache & reset params
              let { dataSourceCode, periodCode, templateType } = isLoaded
                ? {
                  dataSourceCode: reportCreateUpdateForm.dataSourceCode,
                  periodCode: reportCreateUpdateForm.period,
                  templateType: reportCreateUpdateForm.type,
                }
                : reportLoadForm.getParams();
              cachedMobileTemplates.delete(`${dataSourceCode}-${periodCode}-${templateType}`);

              // Reset `update report` form & 'load report' form
              if (isLoaded) {
                if (reportCreateUpdateForm.id === reportLoadForm.selectedReport.id) {
                  reportLoadForm.selectedReport = null;
                }
                reportCreateUpdateForm = createReportFormObject();
              } else {
                if (reportCreateUpdateForm.id === reportLoadForm.selectedReport.id) {
                  reportCreateUpdateForm = createReportFormObject();
                }
                reportLoadForm.selectedReport = null;
              }

              // Reload mobile template list
              updateMobileReportList();

              this.showMessage(result.message);
            })
            .catch((error) => {
              this.handleError(error)
            });
        }
      }
    )
  }

  const FilterWrapperComponent = () => {
    return {
      view: (vnode) => {
        return m('.col-12', vnode.attrs, vnode.children)
      }
    }
  }

  return {
    oninit: () => {
      initializePageData();
      reportLoadForm = newReportLoadForm();
    },
    view: () => {
      return [
        // Report information & form section
        m('.d-flex.flex-column.align-items-end.gap-2.mb-3',
          m('.d-flex.align-items-end.justify-content-between.gap-2.w-100',
            m('.flex-grow-1.d-flex.align-items-center.gap-2',
              m('a.btn.btn-primary[role="button"]', {
                onclick: handleClearButton,
                title: this.getText('LT00001060', 'New Report'),
              },
                m('i.bi.bi-journal-bookmark.me-0.me-sm-2'),
                m('span.d-none.d-sm-inline.ms-1', this.getText('LT00001060', 'New Report')),
              ),
              m('a.btn.btn-primary[role="button"]', {
                onclick: () => { openLoadReportTemplateDialog() },
                title: this.getText('LT00001061', 'Load Report'),
              },
                m('i.bi.bi-journal-bookmark.me-0.me-sm-2'),
                m('span.d-none.d-sm-inline.ms-1', this.getText('LT00001061', 'Load Report')),
              ),
            ),
          ),

          m('.card.shadow-border.w-100',
            m('.card-body.px-4',
              m('form#mobile-report-builder-form.needs-validation[novalidate]', {
                onsubmit: handleReportFormSubmission
              },
                m('.row.g-4',
                  m('.col-12',
                    m('h4.my-2', this.getText('LT00001062', 'Report Information')),
                    m('.card',
                      m('.card-body',
                        m('.row.g-3',
                          m('.col-12.col-md-6.col-lg-6.col-xxl-3.position-relative',
                            m('label.form-label.mandatory[for=report-name]', `${this.getText('LT00001063', 'Report Name')}`),
                            m('input#report-name.form-control[required]', {
                              disabled: mode === MODE.UPDATE,
                              onchange: (e) => { reportCreateUpdateForm.name = e.target.value },
                              value: reportCreateUpdateForm.name,
                            }),
                            m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                          ),
                          m('.col-12.col-md-6.col-lg-6.col-xxl-3.position-relative',
                            m('label.form-label[for=report-data-source]', `${this.getText('LT00001064', 'Data Source')}`),
                            m(SingleSelectComponent, {
                              allowEmpty: false,
                              disabled: mode === MODE.UPDATE,
                              id: 'report-data-source',
                              items: dataSourceOptions,
                              keyOnly: true,
                              required: true,
                              selectedItem: reportCreateUpdateForm.dataSourceCode,
                              onSelect: handleDataSourceChange,
                            }),
                          ),
                          m('.d-none.d-xl-block.w-100.m-0'),
                          m('.col-12.col-md-7.col-lg-3.col-xxl-2',
                            m('label.form-label[for=report-type]', `${this.getText('LT00000117', 'Type')}`),
                            m(ToggleButtonGroupComponent, {
                              ariaLabel: this.getText('LT00000117', 'Type'),
                              buttonType: BUTTON_TYPE.RADIO,
                              buttons: Object.values(REPORT_TEMPLATE_TYPE).map(type => ({
                                checked: reportCreateUpdateForm.type?.toLowerCase() === type.toLowerCase(),
                                id: type,
                                label: type,
                                value: type,
                              })),
                              disabled: mode === MODE.UPDATE,
                              name: 'type',
                              onChange: (e) => { reportCreateUpdateForm.type = e.target.value },
                            }),
                          ),
                          m('.col-12.col-md-7.col-lg-3.col-xxl-2',
                            m('label.form-label', `${this.getText('LT00000150', 'Period')}`),
                            m(ToggleButtonGroupComponent, {
                              ariaLabel: this.getText('LT00000150', 'Period'),
                              buttonType: BUTTON_TYPE.RADIO,
                              buttons: periodOptions?.map(p => ({
                                checked: reportCreateUpdateForm.period === p.code,
                                id: p.code,
                                label: p.name,
                                value: p.code,
                              })),
                              disabled: mode === MODE.UPDATE,
                              name: 'period',
                              onChange: handlePeriodChange,
                            }),
                          ),
                          m('.col-12.col-md-5.col-lg-3.col-xxl-2',
                            m('label.form-label.d-none.d-md-inline-block.my-3'),
                            m('.d-flex.py-1',
                              m(Switch, {
                                checked: reportCreateUpdateForm.includeCurrent,
                                label: `${this.getText('LT00001065', 'Include current')}`,
                                onchange: (e) => {
                                  reportCreateUpdateForm.includeCurrent = e.target.checked;
                                }
                              }),
                            ),
                          ),
                          mode === MODE.CREATE &&
                          m('.col-12',
                            m('label.form-label.d-none.d-md-inline-block'),
                            m('.d-flex.pb-1',
                              m(Switch, {
                                checked: reportCreateUpdateForm.createTodoLinkCode,
                                label: `${this.getText('LT00001066', 'Generate Link Code')}`,
                                onchange: (e) => {
                                  reportCreateUpdateForm.createTodoLinkCode = e.target.checked;
                                }
                              }),
                            ),
                          ),
                          mode === MODE.UPDATE && [
                            m('.d-none.d-xl-block.w-100.m-0'),
                            m('.col-12.col-md-6.col-xxl-3',
                              m('label.form-label[for=link-code]', `${this.getText('LT00001067', 'Link Code')}`),
                              m('input#link-code.form-control[disabled]', {
                                value: reportCreateUpdateForm.linkCode,
                              }),
                            ),
                          ],
                        ),
                      ),
                    ),
                  ),
                  m('.col-12',
                    m('h4.my-2', this.getText('LT00001068', 'Report Access Control')),
                    m('.card',
                      m('.card-body',
                        m('.row.g-3',
                          m('.col-12.col-md-8.col-lg-6.col-xxl-4',
                            m('label.form-label[for=user-role]', `${this.getText('LT00000060', 'User Role')}`),
                            m(SelectCodeMasterCategoryComponent, {
                              id: 'user-role',
                              category: codeMasterType.userRole,
                              selectedItems: reportCreateUpdateForm.userRoleList,
                              onSelect: (selectedItems) => {
                                reportCreateUpdateForm.userRoleList = selectedItems;
                              }
                            }),
                          ),
                        ),
                      ),
                    ),
                  ),
                  m('.col-12.col-lg-6',
                    m('h4.my-2', this.getText("LT00000969", "Report Filter")),
                    m('.card',
                      m('.card-body',
                        m('.row.g-3',
                          m(DynamicReportFilterComponent, {
                            data: {
                              dataSourceCode: reportCreateUpdateForm.dataSourceCode,
                              periodCode: reportCreateUpdateForm.period,
                              parameters: reportCreateUpdateForm.filters,
                              isLoadedFromTemplate: isLoadedFromTemplate
                            },
                            buttonWrapperComponent: FilterWrapperComponent,
                            filterWrapperComponent: FilterWrapperComponent,
                            onChange: (selectedFilters, _isLoadedFromTemplate = false) => {
                              reportCreateUpdateForm.filters = selectedFilters;
                              isLoadedFromTemplate = _isLoadedFromTemplate;
                            },
                          }),
                        ),
                      ),
                    )
                  ),
                  m('.col-12.col-lg-6',
                    m('h4.my-2', this.getText("LT00000970", "Report Structure")),
                    m('.card',
                      m('.card-body',
                        m('.row.g-3',
                          m('.col-12.position-relative',
                            m('label.form-label.mandatory[for=columnHeaders]', `${this.getText("LT00000971", "Column Headers")} (${this.getText("LT00000976", "X-Axis")})`),
                            m(SingleSelectComponent, {
                              id: 'columnHeaders',
                              items: xAxisOptions,
                              selectedItem: reportCreateUpdateForm.xAxis,
                              required: true,
                              onSelect: (updatedItems) => {
                                reportCreateUpdateForm.xAxis = updatedItems;
                              },
                            }),
                            m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                          ),
                          m('.col-12.position-relative',
                            m('label.form-label.mandatory[for=rowHeaders]', `${this.getText("LT00000972", "Row Headers")} (${this.getText("LT00000977", "Y-Axis")})`),
                            m(SortableMultiSelectComponent, {
                              id: 'rowHeaders',
                              items: yAxisOptions,
                              selectedItems: reportCreateUpdateForm.yAxis,
                              required: true,
                              onChange: (updatedItems, updatedOrder) => {
                                reportCreateUpdateForm.yAxis = updatedItems;
                                reportCreateUpdateForm.yAxisOrderedCodes = updatedOrder;
                              },
                            }),
                            m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                          ),
                          m('.col-12.position-relative',
                            m('label.form-label.mandatory[for=dataDisplay]', `${this.getText("LT00000973", "Data Display")} (${this.getText("LT00000978", "Z-Axis")})`),
                            m(SortableMultiSelectComponent, {
                              id: 'dataDisplay',
                              items: zAxisOptions,
                              selectedItems: reportCreateUpdateForm.zAxis,
                              required: true,
                              sortableListAttrs: {
                                listItemAttrs: {
                                  showKeyInTitle: true,
                                }
                              },
                              onChange: (updatedItems, updatedOrder) => {
                                reportCreateUpdateForm.zAxis = updatedItems;
                                reportCreateUpdateForm.zAxisOrderedCodes = updatedOrder;
                              },
                            }),
                            m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                          ),
                        ),
                      ),
                    ),
                  ),
                  m('.col-12',
                    m('h4.my-2', this.getText("LT00001069", "Report Settings")),
                    m('.card',
                      m('.card-body',
                        m('.row.g-3',
                          /**
                           * 20240327
                           * NOTE: Temporarily hide 3 switches: 
                           * 'Show column total'
                           * 'Show row total'
                           * 'Show filter'
                           *  - Please check with clinton.jr -
                           */
                          // m('.col-12.col-sm-4',
                          //   m(Switch, {
                          //     checked: reportCreateUpdateForm.addColumnTotal,
                          //     label: `${this.getText('LT00001070', 'Show column total')}`,
                          //     onchange: (e) => {
                          //       reportCreateUpdateForm.addColumnTotal = e.target.checked;
                          //     }
                          //   }),
                          // ),
                          // m('.col-12.col-sm-4',
                          //   m(Switch, {
                          //     checked: reportCreateUpdateForm.addRowTotal,
                          //     label: `${this.getText('LT00001071', 'Show row total')}`,
                          //     onchange: (e) => {
                          //       reportCreateUpdateForm.addRowTotal = e.target.checked;
                          //     }
                          //   }),
                          // ),
                          m('.col-12.col-sm-4',
                            m(Switch, {
                              checked: reportCreateUpdateForm.excludeEmptyRow,
                              label: `${this.getText('LT00001072', 'Exclude empty row')}`,
                              onchange: (e) => {
                                reportCreateUpdateForm.excludeEmptyRow = e.target.checked;
                              }
                            }),
                          ),
                          m('.col-12.col-sm-4',
                            m(Switch, {
                              checked: reportCreateUpdateForm.zAxisLocation?.toUpperCase() === 'Y',
                              label: `${this.getText('LT00001073', 'Z-Axis on top')}`,
                              onchange: (e) => {
                                if (e.target.checked) {
                                  reportCreateUpdateForm.zAxisLocation = 'Y'; // TOP
                                } else {
                                  reportCreateUpdateForm.zAxisLocation = 'N'; // BOTTOM
                                }
                              }
                            }),
                          ),
                          /**
                           * 20240327
                           * NOTE: Temporarily hide 3 switches: 
                           * 'Show column total'
                           * 'Show row total'
                           * 'Show filter'
                           *  - Please check with clinton.jr -
                           */
                          // m('.col-12.col-sm-4',
                          //   m(Switch, {
                          //     checked: reportCreateUpdateForm.showFilter,
                          //     label: `${this.getText('LT00001074', 'Show filter')}`,
                          //     onchange: (e) => {
                          //       reportCreateUpdateForm.showFilter = e.target.checked;
                          //     }
                          //   }),
                          // ),
                        ),
                      ),
                    ),
                  ),
                ),

                m('.row.g-3.my-3',
                  // Action buttons
                  m('.col-12.d-flex.justify-content-end.gap-2',
                    m('button.btn.btn-primary[type=submit]',
                      this.getText("LT00000022", "Save")
                    ),
                    mode === MODE.CREATE &&
                    m('button.btn.btn-white', {
                      onclick: handleClearButton,
                    }, this.getText("LT00000031", "Clear")),
                    mode === MODE.UPDATE &&
                    m('button.btn.btn-white', {
                      onclick: handleDuplicateButton
                    }, this.getText("LT00001075", "Duplicate")),
                    mode === MODE.UPDATE &&
                    m('button.btn.btn-danger', {
                      onclick: handleDeleteButton(true),
                    }, this.getText("LT00000135", "Delete")),
                  ),
                ),
              ),
            )
          ),
        ),
        this.getBaseViews(),

        /**
         * Load Mobile Report Template Dialog
         */
        m(Dialog, {
          title: this.getText('LT00000964', 'Mobile Report'),
          isOpen: isOpenLoadReportTemplateDialog,
          onClose: () => { closeLoadReportDialog() },
          onOpened: () => { initLoadReportForm() },
          closeOnOutsideClick: false,
          content: [
            m('.container.pt-2.pb-4',
              m('form#load-report-form.row.gy-2.align-items-center.needs-validation[novalidate]', {
                onsubmit: loadMobileReport,
              },
                m('.col-3',
                  m('label.form-label.m-0[for="dialog-form-data-source"]', this.getText('LT00001064', 'Data Source')),
                ),
                m('.col-9',
                  m(SingleSelectComponent, {
                    id: 'dialog-form-data-source',
                    items: dataSourceOptions,
                    keyOnly: true,
                    selectedItem: reportLoadForm.dataSourceCode,
                    onSelect: (selected) => {
                      handleReportLoadFormFieldChange('dataSourceCode', selected);
                    }
                  }),
                ),
                m('.col-3',
                  m('label.form-label.m-0', this.getText('LT00000150', 'Period')),
                ),
                m('.col-9',
                  m(ToggleButtonGroupComponent, {
                    ariaLabel: this.getText('LT00000150', 'Period'),
                    buttonType: BUTTON_TYPE.RADIO,
                    buttons: loadReportPeriodOptions?.map(p => ({
                      checked: reportLoadForm.period === p.code,
                      id: `dialog-form-period-${p.code}`,
                      label: p.name,
                      value: p.code,
                    })),
                    onChange: (e) => {
                      handleReportLoadFormFieldChange('period', e.target.value);
                    },
                  }),
                ),
                m('.col-3',
                  m('label.form-label.m-0', this.getText('LT00000117', 'Type')),
                ),
                m('.col-9',
                  m(ToggleButtonGroupComponent, {
                    ariaLabel: this.getText('LT00000117', 'Type'),
                    buttonType: BUTTON_TYPE.RADIO,
                    buttons: Object.values(REPORT_TEMPLATE_TYPE).map(type => ({
                      checked: reportLoadForm.type === type,
                      id: `dialog-form-type-${type}`,
                      label: type,
                      value: type,
                    })),
                    onChange: (e) => {
                      handleReportLoadFormFieldChange('type', e.target.value);
                    },
                  }),
                ),
                m('.col-3',
                  m('label.form-label.mandatory.m-0', this.getText('LT00008094', 'Mobile Report')),
                ),
                m('.col-9.position-relative',
                  m(SingleSelectComponent, {
                    itemKey: 'id',
                    itemValue: 'name',
                    items: mobileReportOptions,
                    selectedItem: reportLoadForm.selectedReport,
                    required: true,
                    onSelect: (selected) => {
                      reportLoadForm.selectedReport = selected;
                    }
                  }),
                  m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                ),
              )
            ),
            this.getBaseViews()
          ], // content - end
          footer: [
            m('.d-flex.justify-content-end.gap-2',
              m('button.btn.btn-primary[form="load-report-form"][type="submit"]',
                this.getText('LT00000975', 'Load')
              ),
              m('button.btn.btn-danger', {
                disabled: !reportLoadForm.selectedReport?.canDelete,
                onclick: handleDeleteButton(),
              }, this.getText('LT00000135', 'Delete')),
              m('button.btn.btn-white', {
                onclick: () => { closeLoadReportDialog() },
              }, this.getText('LT00000021', 'Close')),
            ),
            this.getBaseViews()
          ], // footer - end
        }),

      ]
    }
  }
}